// Feature-focused landing pages (footer-linked, for feature benefit testing)

const SHOTS = {
  mood: '/shots/mood_sync.png',
  brownies: '/shots/brownie_points.png',
  calendar: '/shots/calendar_reminders.png',
  conflict: '/conflict_real_faces.png',
  streak: '/shots/streak_celebration.png',
};

export const FEATURE_PAGES = {
  'never-forget-anything': {
    path: '/never-forget-anything',
    badge: '📅 Shared Calendar & Reminders',
    h1a: 'Never Forget',
    h1b: 'Anything Important. Ever Again.',
    sub: 'Anniversaries, date nights, her mom\'s birthday, the dentist. CoupleIn keeps it all in one shared calendar: two people, one source of truth, zero "you never told me" fights.',
    heroShot: SHOTS.calendar,
    heroAlt: 'CoupleIn shared calendar and reminders',
    seo: {
      title: 'Never Forget Anything Important | Shared Couple Calendar App',
      description: 'CoupleIn is the shared calendar app for couples. Anniversaries, date nights, appointments. Both of you, always on the same page. Free to start.',
      keywords: 'shared calendar for couples, couple reminder app, never forget anniversary, couples organizer app',
    },
    benefits: [
      { emoji: '🔔', title: 'Alarms that actually save you', text: 'Set it once, get reminded at the right moment. The anniversary panic-run to the petrol station flowers ends today.' },
      { emoji: '👀', title: 'One source of truth', text: 'No more "it\'s in MY calendar". Everything you both need to know lives in one place you both see.' },
      { emoji: '🗓️', title: 'The small stuff too', text: 'Bins night, in-laws visiting, car MOT. The unglamorous stuff that starts 80% of household arguments? Handled.' },
    ],
    othersTitle: 'And once nothing slips through the cracks…',
    othersSub: 'CoupleIn does a lot more than remember.',
  },
  'solve-fights-in-5-minutes': {
    path: '/solve-fights-in-5-minutes',
    badge: '🕊️ Guided Conflict Resolution',
    h1a: 'Solve Any Fight',
    h1b: 'in 5 Minutes or Less.',
    sub: 'CoupleIn\'s Guided Mode walks you through a fair-turn conversation that stops the shouting, makes sure you both feel heard, and gets you back on the same team. No therapist required.',
    heroShot: SHOTS.conflict,
    heroAlt: 'CoupleIn guided conflict resolution mode',
    seo: {
      title: 'Solve Any Fight in 5 Minutes or Less | Couples Conflict Resolution App',
      description: 'CoupleIn\'s Guided Mode walks couples through a fair-turn conversation that resolves heated arguments in 5 minutes or less. Free to start.',
      keywords: 'conflict resolution app for couples, stop fighting with partner, couples argument app, relationship communication app',
    },
    benefits: [
      { emoji: '⏱️', title: 'Timed, fair turns', text: 'Each of you gets uninterrupted time to speak. No talking over each other, no monologues. The structure does the refereeing.' },
      { emoji: '🪞', title: 'Repeat what you heard', text: 'The magic step: you each mirror back what your partner said. Half of most fights die right here. It was a misunderstanding all along.' },
      { emoji: '🤝', title: 'End with an agreement', text: 'Every session closes with one concrete thing you\'ll each do. Fights stop repeating when they actually get resolved.' },
    ],
    othersTitle: 'And when you\'re not fighting…',
    othersSub: 'CoupleIn keeps the good days good.',
  },
  'love-with-intentionality': {
    path: '/love-with-intentionality',
    badge: '⭐ Brownie Points',
    h1a: 'One Action Says More',
    h1b: 'Than 1000 Words.',
    sub: 'Love with intentionality. CoupleIn turns everyday gestures like the coffee run, the foot rub, and the surprise flowers into Brownie Points you exchange with each other. Kindness becomes a game you both win.',
    heroShot: SHOTS.brownies,
    heroAlt: 'CoupleIn Brownie Points, exchange kindness daily',
    seo: {
      title: 'One Action Says More Than 1000 Words | Brownie Points for Couples',
      description: 'CoupleIn turns everyday kindness into Brownie Points you exchange with your partner. Love with intentionality, make every gesture count. Free to start.',
      keywords: 'couples rewards app, brownie points couple, relationship gamification, appreciation app for couples, acts of love',
    },
    benefits: [
      { emoji: '🎯', title: 'Kindness on purpose', text: 'A wishlist of what your partner actually wants, so your effort lands every single time instead of guessing.' },
      { emoji: '🏆', title: 'Effort gets noticed', text: 'Every gesture is logged and celebrated. The invisible work you both do finally gets the credit it deserves.' },
      { emoji: '🔥', title: 'It becomes a habit', text: 'Streaks and playful competition keep you both showing up daily. Small actions, compounded, change everything.' },
    ],
    othersTitle: 'And it doesn\'t stop at points…',
    othersSub: 'CoupleIn covers the whole relationship.',
  },
  'stay-connected-in-60-seconds': {
    path: '/stay-connected-in-60-seconds',
    badge: '💛 60-Second Daily Check-in',
    h1a: 'Stay Connected',
    h1b: 'in 60 Seconds a Day.',
    sub: 'CoupleIn\'s daily mood sync catches the bad days before they become bad weeks. One tap each, once a day, and you always know how your partner is really doing.',
    heroShot: SHOTS.mood,
    heroAlt: 'CoupleIn mood sync, 60-second daily check-in for couples',
    seo: {
      title: 'Stay Connected in 60 Seconds a Day | Couples Mood Check-in App',
      description: 'CoupleIn\'s 60-second daily mood check-in keeps couples in sync. Catch bad days before they become bad weeks. Free to start.',
      keywords: 'couples check in app, mood tracker for couples, daily relationship check in, mood sync app, stay connected with partner',
    },
    benefits: [
      { emoji: '🌡️', title: 'Know before you ask', text: 'One glance tells you how their day is really going, so you come home prepared with pizza, patience, or a hug.' },
      { emoji: '🚧', title: 'Catch drift early', text: 'Most couples don\'t blow up. They drift. A daily pulse keeps small distances from quietly growing into big ones.' },
      { emoji: '🔥', title: 'Streaks make it stick', text: 'Check-in streaks turn one tiny habit into a ritual you both protect. 73% of users hit a 30-day streak in their first 3 months.' },
    ],
    othersTitle: 'And once you\'re in sync…',
    othersSub: 'CoupleIn handles the rest of the relationship too.',
  },
};

export const OTHER_FEATURES = [
  { id: 'never-forget-anything', shot: SHOTS.calendar, title: 'Shared Calendar', text: 'Never forget an anniversary, date night, or appointment again.' },
  { id: 'solve-fights-in-5-minutes', shot: SHOTS.conflict, title: 'Guided Mode', text: 'Solve heated arguments in 5 minutes or less with fair-turn conversations.' },
  { id: 'love-with-intentionality', shot: SHOTS.brownies, title: 'Brownie Points', text: 'Turn everyday kindness into a game you both win.' },
  { id: 'stay-connected-in-60-seconds', shot: SHOTS.mood, title: '60-Second Check-in', text: 'Sync moods daily and catch bad days before they become bad weeks.' },
];
